import React, { FC, useEffect, useMemo, useState } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { ICartEntry } from 'features/cart/types';
import {
  mapLegacyToMenuSelections,
  mapMenuSelectionsToICartEntry,
} from 'features/cart/utils/minimal-cart';
import withMenuEmptyState from 'features/menu/components/with-menu-empty-state';
import {
  mapCartEntriesToMenuSelections,
  mapMinimalCartEntryToBackendEntry,
  mapMinimalFavoriteToCartEntries,
} from 'features/menu/pages/favorites-page/minimal-favorites/utils';
import { StyleSheet } from 'react-native';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { CardModal } from 'components/card-modal';
import ConfirmDialog from 'components/confirm-dialog';
import { FavoriteInput } from 'components/favorite-input';
import ItemActions from 'components/item-actions';
import { Product } from 'components/product';
import { SaveFavoriteOrderUnsaved } from 'components/save-favorite-order-unsaved';
import { toaster } from 'components/toaster/toaster';
import { Box } from 'components/ui';
import type { BoxProps } from 'components/ui';
import {
  GetUserMinimalFavoritesDocument,
  IMinimalFavoriteFragment,
  useDeleteMinimalFavoriteMutation,
  useGetUserMinimalFavoriteQuery,
  useUpdateMinimalFavoriteMutation,
} from 'generated/graphql-gateway';
import LoyaltyLoader from 'pages/loyalty/loyalty-loader/loyalty-loader';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useFavoritesContext } from 'state/favorites';
import { t } from 'state/intl/translations';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { getDesignToken, primitive, spacing } from 'styles/constants';
import { getMinimalCartEntryRef } from 'utils/menu/get-cart-entry-ref';
import { mapBackendToFrontend } from 'utils/reorder';
import { routes } from 'utils/routing';

const StyledCard = ({ style, ...props }: BoxProps) => (
  <Box {...props} style={[styles.card, style]} />
);

const StyledItemActions = ({ style, ...props }: BoxProps) => (
  <Box {...props} style={[styles.itemActions, style]} />
);

const mapSelections = (entry: ICartEntry) => {
  return (entry.children ?? []).map((child, index) => ({
    id: `${entry.id}-${child.id}-${index}`,
    element: `${child.quantity}x ${child.name}`,
  }));
};

const MinimalFavoriteEditorPage: FC = () => {
  const router = useRouter();
  const {
    setShouldRefetchFavorites,
    tempFavorite,
    setFavorite,
    cancelEditing,
  } = useFavoritesContext();
  const { favoriteId } = useLocalSearchParams<{ favoriteId: string }>();
  const { indexedMenu, indexedOffers, indexedRewards } = useUnifiedMenuContext();
  const { posServiceMode } = useMenuQueryOptions();

  const [entries, setEntries] = useState<ICartEntry[]>([]);
  const [favoriteName, setFavoriteName] = useState('');
  // The name as loaded from the server, before any edit in this session — used at save time to
  // detect whether the guest actually renamed the favorite, independent of whether that stored
  // name happens to match (or not) the entry's current-locale live name (see `handleSaveEdits`).
  const [originalFavoriteName, setOriginalFavoriteName] = useState('');
  const [favoriteRef, setFavoriteRef] = useState<string | null>(null);
  const [initialSnapshot, setInitialSnapshot] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [unSavedChangesModalOpen, setUnSavedChangesModalOpen] = useState(false);
  const [removeFavoriteDialog, setRemoveFavoriteDialog] = useState(false);

  const [deleteFavorite] = useDeleteMinimalFavoriteMutation({
    onCompleted: () => {
      toaster.positive(t('removeFavoriteOrderSucess'));
      setShouldRefetchFavorites(true);
      cancelEditing();
      router.dismiss();
    },
  });

  const [updateFavorite] = useUpdateMinimalFavoriteMutation({
    onCompleted: () => {
      setShouldRefetchFavorites(true);
      cancelEditing();
      router.dismissTo(routes.favorites);
    },
  });

  const { loading } = useGetUserMinimalFavoriteQuery({
    variables: {
      favoriteId,
    },
    onCompleted: data => {
      const favorite = data?.userMinimalFavorite as IMinimalFavoriteFragment | null | undefined;
      if (!favorite) {
        return;
      }

      const hasActiveTempFavorite =
        tempFavorite?.favoriteId === favoriteId && (tempFavorite?.entries?.length ?? 0) > 0;

      const mappedEntries = mapMinimalFavoriteToCartEntries({
        favorite,
        menu: indexedMenu,
        indexedOffers,
        indexedRewards,
        posServiceMode,
        url: routes.menu,
      });

      // Preserve in-progress edits made while navigating to menu and back.
      if (!hasActiveTempFavorite) {
        setEntries(mappedEntries);
        setFavoriteName(favorite.name ?? '');
        setOriginalFavoriteName(favorite.name ?? '');
        setFavoriteRef(favorite.ref ?? null);
        setFavorite({
          favoriteId: favorite.favoriteId,
          name: favorite.name,
          ref: favorite.ref,
          entries: mappedEntries.map(mapMinimalCartEntryToBackendEntry) as any,
        } as any);
      }

      setInitialSnapshot(
        JSON.stringify({
          name: favorite.name ?? '',
          entries: mapCartEntriesToMenuSelections(mappedEntries),
        })
      );
    },
  });

  useEffect(() => {
    if (!tempFavorite?.favoriteId || tempFavorite.favoriteId !== favoriteId) {
      return;
    }

    const tempEntries = (tempFavorite.entries as any[]) ?? [];
    if (!tempEntries.length) {
      return;
    }

    const frontendEntries = tempEntries.map(entry => mapBackendToFrontend(entry as any));
    const menuSelections = mapLegacyToMenuSelections(frontendEntries);
    const mappedEntries = mapMenuSelectionsToICartEntry(menuSelections, {
      menu: indexedMenu,
      indexedOffers,
      indexedRewards,
      posServiceMode,
      url: routes.menu,
    });

    setEntries(mappedEntries);
    setFavoriteName(tempFavorite.name ?? favoriteName);
  }, [
    favoriteId,
    favoriteName,
    indexedMenu,
    indexedOffers,
    indexedRewards,
    posServiceMode,
    tempFavorite,
  ]);

  const hasUnsavedChanges = useMemo(() => {
    if (!initialSnapshot) {
      return false;
    }
    return (
      JSON.stringify({
        name: favoriteName,
        entries: mapCartEntriesToMenuSelections(entries),
      }) !== initialSnapshot
    );
  }, [entries, favoriteName, initialSnapshot]);

  const handleCloseEditor = () => {
    if (hasUnsavedChanges) {
      setUnSavedChangesModalOpen(true);
      return;
    }
    cancelEditing();
    router.dismiss();
  };

  const handleSaveEdits = async () => {
    setErrorMessage('');
    logEvent(CustomEventNames.EDIT_FAVORITE, EventTypes.UserContent, {
      'Favorite Id': favoriteId,
    });

    if (!favoriteName.trim()) {
      setErrorMessage(t('saveFavoriteOrderNameError'));
      return;
    }

    // `ref` must be preserved by default: `MinimalRecentOrderFavoriteButton` matches an order to
    // its favorite via `favorite.ref === order.rbiOrderId`, so clearing `ref` on every save would
    // break that match for any multi-item order-favorite (and duplicate on next favorite/tap) —
    // this is not specific to quick-favorites.
    //
    // `MinimalFavoriteCard` separately treats a matching `getMinimalCartEntryRef(singleEntry) ===
    // ref` as proof the favorite is an untouched quick-favorite and overrides its header with the
    // live item name (see that component). If the guest actually renames a single-item
    // quick-favorite here — i.e. the trimmed name differs from what was loaded when the editor
    // opened, not merely from the entry's current-locale live name, which can legitimately differ
    // without any edit — that ref would otherwise still match post-save and silently discard the
    // new custom title on the next render. Only that specific case clears the ref.
    const trimmedName = favoriteName.trim();
    const singleEntry = entries.length === 1 ? entries[0] : undefined;
    const wasQuickFavorite =
      !!singleEntry && !!favoriteRef && getMinimalCartEntryRef(singleEntry) === favoriteRef;
    const wasRenamed = trimmedName !== originalFavoriteName.trim();
    const refToSave = wasQuickFavorite && wasRenamed ? null : favoriteRef;

    await updateFavorite({
      variables: {
        favoriteId,
        input: {
          ref: refToSave,
          entries: mapCartEntriesToMenuSelections(entries),
          name: trimmedName,
        },
      },
      refetchQueries: [{ query: GetUserMinimalFavoritesDocument }],
    });
  };

  const handleRemoveItem = (cartId?: string) => {
    const target = entries.find(
      (entry, index) => (entry.cartId ?? `${entry.id}-${index}`) === cartId
    );
    if (!target) {
      return;
    }

    if (entries.length <= 1) {
      setRemoveFavoriteDialog(true);
      return;
    }

    setEntries(currentEntries =>
      currentEntries.filter((entry, index) => (entry.cartId ?? `${entry.id}-${index}`) !== cartId)
    );
  };

  const removeFavorite = () => {
    deleteFavorite({
      variables: {
        favoriteId,
      },
      refetchQueries: [{ query: GetUserMinimalFavoritesDocument }],
    });
    setRemoveFavoriteDialog(false);
  };

  const discardChanges = () => {
    router.replace(routes.favorites);
    cancelEditing();
  };

  const favoriteEntries = entries.map((entry, index) => {
    const stableCartId = entry.cartId ?? `${entry.id}-${index}`;

    return (
      <StyledCard key={stableCartId}>
        <Product
          quantity={entry.quantity}
          name={entry.name}
          image={JSON.stringify(entry.image ?? {})}
          selections={mapSelections(entry)}
          quantityBadgeTextStyle={{
            color: primitive.$mayoEggWhite,
          }}
          quantityBadgeStyle={{
            top: '-10px',
            height: '20px',
            width: '20px',
            justifyContent: 'center',
          }}
        />
        <StyledItemActions>
          <ItemActions
            showEdit={false}
            onEdit={() => undefined}
            onRemove={() => handleRemoveItem(stableCartId)}
          />
        </StyledItemActions>
      </StyledCard>
    );
  });

  return (
    <CardModal
      testID="favorite-editor"
      headerText={t('editFavorite')}
      onDismiss={handleCloseEditor}
      scrollView
      keyboardAwareFooter
      footer={
        <ActionButton fullWidth onPress={handleSaveEdits}>
          {t('update')}
        </ActionButton>
      }
    >
      {loading ? (
        <LoyaltyLoader />
      ) : (
        <>
          <Box style={styles.fieldWrapper}>
            <FavoriteInput
              errorMessage={errorMessage}
              setErrorMessage={setErrorMessage}
              onChange={setFavoriteName}
              testID="favorite-editor-name-input"
              favoriteName={favoriteName}
            />
          </Box>
          {favoriteEntries}
          <Box style={styles.fieldWrapper}>
            <ActionButton
              onPress={() => {
                setFavorite({
                  favoriteId,
                  name: favoriteName,
                  ref: favoriteRef,
                  entries: entries.map(mapMinimalCartEntryToBackendEntry) as any,
                } as any);
                queueMicrotask(() => {
                  router.replace(routes.menu);
                });
              }}
              startIcon={<Icon variant="add" aria-hidden />}
              variant={ActionButtonVariants.OUTLINE}
              size={ActionButtonSizes.SMALL}
              fullWidth
            >
              {t('addItems')}
            </ActionButton>
          </Box>
        </>
      )}
      {unSavedChangesModalOpen && (
        <SaveFavoriteOrderUnsaved
          onSave={handleSaveEdits}
          onDiscard={discardChanges}
          onDismiss={() => setUnSavedChangesModalOpen(false)}
        />
      )}
      {removeFavoriteDialog && (
        <ConfirmDialog
          showDialog
          testID="remove-favorite-order-modal"
          heading={t('favoriteRemoveAllModalHeader')}
          body={t('favoriteRemoveAllModalBody')}
          cancelLabel={t('cancel')}
          confirmLabel={t('deleteButtonText')}
          onCancel={() => setRemoveFavoriteDialog(false)}
          onConfirm={removeFavorite}
          modalAppearanceEventMessage="Remove Favorite"
        />
      )}
    </CardModal>
  );
};

export default withMenuEmptyState(MinimalFavoriteEditorPage);

const styles = StyleSheet.create({
  card: {
    backgroundColor: getDesignToken('background-pattern'),
    borderBottomColor: primitive.$greyFour,
    borderBottomWidth: 1,
    padding: spacing.$4,
  },
  fieldWrapper: {
    margin: spacing.$4,
  },
  itemActions: {
    marginBottom: spacing.$0,
    marginLeft: spacing.$0,
    marginRight: spacing.$16,
    marginTop: spacing.$2,
  },
});
